import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import { FleetType } from "../../../models/fleet-type";
import useFleetTypeService from "../../../services/backoffice/hooks/useFleetTypeService";
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import { useNavigate } from "react-router-dom";
import FleetTypeForm from "./create-fleet-type-form";
import { FleetTypeFilter } from "../../../models/requests/create-fleet-type-request";

interface FleetTypesListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const FleetTypesList: React.FC<FleetTypesListProps> = ({ isModal = false, open = false, onClose }) => {
    const [fleetTypes, setFleetTypes] = useState<FleetType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<FleetTypeFilter>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getFleetTypes, deleteFleetType, activateFleetType, deactivateFleetType } = useFleetTypeService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editFleetTypeId, setEditFleetTypeId] = useState<string | null>(null);


    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadFleetTypes() {
            setLoading(true);
            try {
                const response = await getFleetTypes(filter);
                setFleetTypes(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar tipos de frota.");
            } finally {
                setLoading(false);
            }
        }
        loadFleetTypes();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir este tipo de frota?");
            if (result) {
                await deleteFleetType(id);
                toast.success("Tipo de frota excluído com sucesso", ToasterConfig.success);
                setFleetTypes(fleetTypes.filter((ft) => ft.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir tipo de frota", ToasterConfig.error);
        }
    };

    const handleActivation = async (fleetType: FleetType) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${fleetType.isActive ? 'desativar' : 'ativar'} este tipo de frota?`);
            if (result) {
                if (fleetType.isActive) {
                    await deactivateFleetType(fleetType.id);
                } else {
                    await activateFleetType(fleetType.id);
                }
                setFilter({ ...filter }); // Atualiza a lista
                toast.success(`Tipo de frota ${fleetType.isActive ? 'desativado' : 'ativado'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status do tipo de frota", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditFleetTypeId(id);
        setModalOpen(true);
    };

    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Tipos de Frota
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código de tipo de frota"
                                    value={filter?.code ?? ''}
                                    onChange={(event) => setFilter({ ...filter, code: event.target.value })}
                                />
                            </Grid.Column>


                            <Grid.Column width={6}>
                                <label htmlFor="name">Tipo de frota</label>
                                <Input
                                    fluid
                                    id="name"
                                    icon="search"
                                    type="text"
                                    placeholder="Tipo de frota"
                                    value={filter?.name ?? ''}
                                    onChange={(event) => setFilter({ ...filter, name: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {fleetTypes?.map((fleetType) => (
                        <Table.Row key={fleetType.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar tipo de frota"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => {
                                                        setEditFleetTypeId(fleetType.id);
                                                        setModalOpen(true);
                                                    }}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir tipo de frota"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(fleetType.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={fleetType.isActive ? "Desativar tipo de frota" : "Ativar tipo de frota"}
                                            trigger={
                                                <Button
                                                    color={fleetType.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(fleetType)}
                                                    icon={fleetType.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{fleetType.code}</Table.Cell>
                            <Table.Cell>{fleetType.name}</Table.Cell>
                            <Table.Cell>
                                <Label color={fleetType.isActive ? 'green' : 'red'}>
                                    {fleetType.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '620px', maxWidth: '95vw', padding: '20px' }}
            >
                <FleetTypeForm
                    isModal
                    open={modalOpen}
                    fleetTypeId={editFleetTypeId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditFleetTypeId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>

            {formContent}
        </>
    );
};

export default FleetTypesList;
